import React, { useState, useEffect } from "react";
import { Card, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import dataModel from "../../img/api/dataModel.png";
import dataSchema from "../../img/api/dataSchema.png";

function ApiTechnicalDataModels() {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [layerDetails, setLayerDetails] = useState({});
  const [selectedLayer, setSelectedLayer] = useState(null);

  const apiUsername = process.env.REACT_APP_API_USERNAME;
  const apiPassword = process.env.REACT_APP_API_PASSWORD;
  const auth = { username: apiUsername, password: apiPassword };

  const cleanLayerName = (name) => name.replace(/^api:/, "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://api.osiris.brussels/geoserver/rest/workspaces/api/layers.json",
          { auth }
        );
        const layers = response.data.layers.layer || [];
        setTables(layers);
        
        if (layers.length > 0) {
          setSelectedLayer(layers[0].name);
        }

        layers.forEach((layer) => {
          const cleanedName = cleanLayerName(layer.name);
          fetchLayerDetails(cleanedName);
        });
      } catch (error) {
        console.error("Error while loading the layers:", error);
      }
    };
    fetchData();
  }, []);

  const fetchLayerDetails = async (layerName) => {
    try {
      const layerUrl = `https://api.osiris.brussels/geoserver/rest/workspaces/api/datastores/Osiris/featuretypes/${encodeURIComponent(layerName)}.json`;
      const response = await axios.get(layerUrl, { auth });
      const featureType = response.data.featureType;
      const description = featureType.abstract || t("api-technical-datamodels-no-description");
      
      const attributes = featureType.attributes.attribute || [];
      const formattedAttributes = attributes.map(attr => ({
        name: attr.name,
        description: attr.description 
          ? attr.description["en-US"] || Object.values(attr.description)[0] 
          : t("api-technical-datamodels-no-description")
      }));
      
      setLayerDetails(prevDetails => ({
        ...prevDetails,
        [layerName]: { attributes: formattedAttributes, description },
      }));
    } catch (error) {
      console.error("Erreur lors du chargement des détails de la couche:", error);
    }
  };

  const normalizedSelectedLayer = selectedLayer ? cleanLayerName(selectedLayer) : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="text-2xl font-bold text-blue-800 dark:text-yellow-800">
        {t("api-technical-datamodels-title")}
      </div>

      <p className="text-lg text-gray-800 dark:text-gray-200">
        {t("api-technical-datamodels-1")}
      </p>

      <div className="text-xl font-bold text-blue-700 dark:text-white">
        {t("api-technical-datamodels-subtitle-0")}
      </div>

      <div className="flex gap-4 ">
        <Card className="w-2/5 overflow-scroll">
          <table className="w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                  <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                    {t("api-technical-datamodels-column-layer-name")}
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {tables.map((table, index) => (
                <tr 
                  key={index} 
                  className={`border-b border-blue-gray-50 transition-colors duration-300 hover:bg-blue-50
                    ${selectedLayer === table.name ? 'bg-blue-100' : ''}`}
                >
                  <td 
                    className="p-4 cursor-pointer"
                    onClick={() => setSelectedLayer(table.name)}
                  >
                    <Typography variant="medium" color="blue-gray" className="font-normal">
                      {cleanLayerName(table.name)}
                    </Typography>
                    {layerDetails[cleanLayerName(table.name)]?.description && (
                      <Typography variant="small" color="gray" className="italic">
                        {layerDetails[cleanLayerName(table.name)].description}
                      </Typography>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="w-3/5 overflow-scroll">
          {normalizedSelectedLayer && layerDetails[normalizedSelectedLayer]?.attributes?.length > 0 ? (
            <table className="w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                    <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                      {t("api-technical-datamodels-column-attribute-name")}
                    </Typography>
                  </th>
                  <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                    <Typography variant="medium" color="blue-gray" className="font-bold leading-none opacity-70">
                      {t("api-technical-datamodels-column-description-name")}
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {layerDetails[normalizedSelectedLayer].attributes.map((attr, index) => (
                  <tr key={index} className="border-b border-blue-gray-50">
                    <td className="p-4">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {attr.name || "-"}
                      </Typography>
                    </td>
                    <td className="p-4">
                      <Typography variant="medium" color="blue-gray" className="font-normal">
                        {attr.description || "-"}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-full p-4 text-gray-500">
              {selectedLayer ? t('data-loading-message') : t('api-technical-datamodels-select-layer-attribute')}
            </div>
          )}
        </Card>
      </div>

      <div className="text-xl font-bold text-blue-700 dark:text-white">
        {t("api-technical-datamodels-subtitle-2")}
      </div>

      <p className="text-lg text-gray-800 dark:text-gray-200">
        {t("api-technical-datamodels-10")}
      </p>

      <img className="max-w-[800px] object-cover object-center" src={dataSchema} loading="eager" alt="Archi" />

      <div className="text-xl font-bold text-blue-700 dark:text-white">
        {t("api-technical-datamodels-subtitle-1")}
      </div>

      <p className="text-lg text-gray-800 dark:text-gray-200">
        {t("api-technical-datamodels-8")}
      </p>

      <p className="text-lg text-gray-800 dark:text-gray-200">
        {t("api-technical-datamodels-9")}
      </p>

      <img className="max-w-[800px] object-cover object-center" src={dataModel} loading="eager" alt="Archi" />
    </div>
  );
}

export default ApiTechnicalDataModels;